import { useCallback } from 'react';

import type { Transaction } from 'prosemirror-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { mergeTranscriptions } from '../effects/mergeTranscriptions';
import {
	type VoiceDictationResult,
	VoiceDictationState,
	VoiceDictationStopReason,
} from '../effects/WebSpeechToTextModels';

import { useTranscriptionToEditorText } from './useTranscriptionToEditorText';

type EditorExecuteFn = (fn: (arg: { tr: Transaction }) => Transaction) => void;

export type EditorApiStub =
	| {
			core?: {
				actions?: {
					execute?: EditorExecuteFn;
				};
			};
	  }
	| undefined;

/**
 * Encapsulates the onVoiceDictationUpdate callback and the underlying
 * useTranscriptionToEditorText helpers. Intended to be shared between
 * chat-prompt-input and inline-chat-prompt consumers.
 */
export const useVoiceDictationEditorHandler = (editorApi: EditorApiStub) => {
	const {
		handleTranscriptUpdate,
		finalizeTranscriptedText,
		rejectTranscriptedText,
		disposeTranscriptedText,
	} = useTranscriptionToEditorText();

	const onVoiceDictationUpdate = useCallback(
		(result: VoiceDictationResult) => {
			switch (result.state) {
				case VoiceDictationState.Recording:
					if (!editorApi?.core?.actions?.execute) {
						return;
					}
					if (
						FeatureGates.getExperimentValue(
							'ai_speech_to_text_perf_enhancement',
							'isEnabled',
							false,
						)
					) {
						const ghost = result.ghostTranscription || '';
						const confirmed = result.transcription || '';
						const { text: textToShow, isFullyResolved } = mergeTranscriptions(confirmed, ghost);

						if (textToShow) {
							editorApi.core.actions.execute(({ tr }) =>
								handleTranscriptUpdate(textToShow, tr, isFullyResolved),
							);
						}
					} else {
						editorApi.core.actions.execute(({ tr }) =>
							handleTranscriptUpdate(result.transcription, tr),
						);
					}
					break;
				case VoiceDictationState.Stopped:
					if (
						FeatureGates.getExperimentValue(
							'ai_speech_to_text_perf_enhancement',
							'isEnabled',
							false,
						)
					) {
						if (result.stopReason !== VoiceDictationStopReason.StoppedRejected) {
							editorApi?.core?.actions?.execute?.(({ tr }) => finalizeTranscriptedText(tr));
						}
					} else {
						if (result.stopReason === VoiceDictationStopReason.TimeoutAccepted) {
							disposeTranscriptedText();
						}
					}
					break;
				default:
					break;
			}
		},
		[handleTranscriptUpdate, editorApi, finalizeTranscriptedText, disposeTranscriptedText],
	);

	return {
		onVoiceDictationUpdate,
		handleTranscriptUpdate,
		finalizeTranscriptedText,
		rejectTranscriptedText,
		disposeTranscriptedText,
	};
};
